type PopupTrigger = { type: 'load', delay: number } | { type: 'visible', selector: string }

interface PopupConfig {
    key: string
    dateFrom: string | null
    dateTo: string | null
    repeatAfterDays: number | null
    maxViews: number | null
    trigger: PopupTrigger
}

interface PopupState {
    views: number
    lastViewAt: number
    dismissed?: boolean
}

const today = (): string => {
    const date = new Date()
    return [date.getFullYear(), date.getMonth() + 1, date.getDate()].map(part => String(part).padStart(2, '0')).join('-')
}

/** Another dialog, the mobile menu, the catalogue filter or the photo gallery is open. */
const overlayIsOpen = (): boolean =>
    document.body.classList.contains('u-hiddenOverflow') || document.documentElement.classList.contains('lg-on')

let shownThisPage = false

/**
 * Site pop-up, opened after a delay or once a target element is in view, while the visitor's stored state for its key allows another view.
 * Of several pop-ups on a page, only the first to open is shown.
 */
export const Popup = (config: PopupConfig) => ({
    storageKey: `AppPopup-${config.key}`,
    opened: false,

    init() {
        const state = this.readState()
        if (state === false || !this.isScheduled() || !this.allowsView(state)) return

        const open = (): boolean => {
            if (shownThisPage) return true
            if (overlayIsOpen()) return false
            if (!this.writeState({ ...state, views: (state?.views ?? 0) + 1, lastViewAt: Date.now() })) return true
            shownThisPage = true
            this.opened = true
            this.$dispatch('open-modal', { id: this.$el.closest('dialog')?.id })
            this.$el.focus()
            return true
        }

        if (config.trigger.type === 'load') {
            const openWhenFree = () => open() || setTimeout(openWhenFree, 1000)
            setTimeout(openWhenFree, config.trigger.delay)
            return
        }

        const target = this.findTarget(config.trigger.selector)
        target && new IntersectionObserver((entries, observer) => {
            entries.some(entry => entry.isIntersecting) && open() && observer.disconnect()
        }, { rootMargin: '0px 0px -20% 0px' }).observe(target)
    },

    findTarget(selector: string): Element | null {
        try {
            return document.querySelector(selector)
        } catch {
            return null
        }
    },

    isScheduled(): boolean {
        const date = today()
        return !(config.dateFrom && date < config.dateFrom) && !(config.dateTo && date > config.dateTo)
    },

    allowsView(state: PopupState | null): boolean {
        if (!state) return true
        if (state.dismissed) return false

        const maxViews = config.maxViews ?? (config.repeatAfterDays ? Infinity : 1)
        if (state.views >= maxViews) return false

        return !config.repeatAfterDays || Date.now() - state.lastViewAt >= config.repeatAfterDays * 864e5
    },

    /** Returns false when storage is unavailable, so the pop-up never shows without a way to remember it. */
    readState(): PopupState | null | false {
        let stored: string | null
        try {
            stored = localStorage.getItem(this.storageKey)
        } catch {
            return false
        }

        try {
            const state = JSON.parse(stored ?? 'null')
            return typeof state?.views === 'number' ? state : null
        } catch {
            return null
        }
    },

    writeState(state: PopupState): boolean {
        try {
            localStorage.setItem(this.storageKey, JSON.stringify(state))
            return true
        } catch {
            return false
        }
    },

    PopupButton: {
        ['@click']() {
            const state = this.readState()
            state !== false && this.writeState({ views: state?.views ?? 1, lastViewAt: state?.lastViewAt ?? Date.now(), dismissed: true })
            this.$el.closest('dialog')?.close()
        }
    }
})
